import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fieldId } from '../../auth/CompanyFields';
import { t } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Loading } from '../belege/Pages';
import { useClientApp } from '../context';
import { lohnPath } from './labels';
import { ShareLink } from './Links';
import { useLohnPage } from './nav';
import { QuestionnaireFields, useQuestionnaire } from './Questionnaire';
import type { NewLink, Rehire } from './types';

// …/neu: neuer Mitarbeiter; …/neu?wieder=<id>: ausgeschiedenen Mitarbeiter wieder einstellen
export function NewEmployeePage({ clientId }: { clientId: string }) {
  const [params] = useSearchParams();
  const wieder = params.get('wieder');
  return wieder ? <RehirePage key={wieder} clientId={clientId} id={wieder} /> : <NewEmployee clientId={clientId} />;
}

// Mitarbeiter anlegen. Am einfachsten per Link: Der Mandant trägt nur Name und Beschäftigung ein, der Mitarbeiter füllt
// den Rest selbst aus. Oder der Mandant füllt den ganzen Personalfragebogen aus. Die Lohnbuchhaltung prüft und meldet an.
function NewEmployee({ clientId }: { clientId: string }) {
  const { clientName } = useClientApp();
  const navigate = useNavigate();
  const toast = useToast();
  const name = clientName(clientId);
  useLohnPage({ title: t('Mitarbeiter anlegen'), clientId, clientName: name, sub: t('Mitarbeiter anlegen') });
  const [mode, setMode] = useState<'link' | 'selbst'>('link');
  // beim Wechsel bleiben die Eingaben erhalten
  const q = useQuestionnaire(mode === 'link' ? 'firma' : 'alles');
  const [link, setLink] = useState<NewLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = q.check();
    if (problem) {
      toast(problem);
      return;
    }
    setBusy(true);
    try {
      if (mode === 'link') {
        setLink(await api.post<NewLink>(`/api/app/lohn/${clientId}/links`, q.data()));
        window.scrollTo({ top: 0 });
        return;
      }
      await api.post(`/api/app/lohn/${clientId}/employees`, q.formData());
      toast(t('Mitarbeiter angelegt · Ihre Lohnbuchhaltung hat die Angaben bekommen'));
      navigate(lohnPath(clientId), { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  if (link) {
    return (
      <>
        <div className="hello">
          <h2>{t('Link für {name} ist fertig', { name: link.name })}</h2>
          <p>{t('{firm} · gültig bis {date}', { firm: name, date: fmtDate(link.expiresAt) })}</p>
        </div>
        <div className="card linkready">
          <ShareLink link={link} firm={name} />
        </div>
        <div className="row linkdone">
          <Link className="btn2" to={lohnPath(clientId)}>
            {t('Fertig')}
          </Link>
        </div>
      </>
    );
  }

  const choice = (value: 'link' | 'selbst', title: string, text: string) => (
    <label className={mode === value ? 'on' : undefined}>
      <input
        type="radio"
        name="who"
        checked={mode === value}
        onChange={() => {
          setMode(value);
          setError(null);
          q.f.setErrors({});
        }}
      />
      <span>
        <b>{title}</b>
        <small>{text}</small>
      </span>
    </label>
  );

  return (
    <>
      <div className="hello">
        <h2>{t('Mitarbeiter anlegen')}</h2>
        <p>
          {name} · {t('Ihre Kanzlei prüft die Angaben und meldet den Mitarbeiter an.')}
        </p>
      </div>
      <form className="form maform" onSubmit={submit} noValidate>
        <fieldset className="who">
          <legend>{t('Wer trägt die persönlichen Angaben ein?')}</legend>
          {choice(
            'link',
            t('Mitarbeiter per Link'),
            t(
              'Sie tragen nur Name, Gehalt und Arbeitszeit ein. Ihr Mitarbeiter füllt den Rest am Handy aus und fotografiert den Ausweis.',
            ),
          )}
          {choice('selbst', t('Ich fülle alles selbst aus'), t('Sie haben alle Angaben und den Ausweis zur Hand.'))}
        </fieldset>
        <QuestionnaireFields q={q} linkHint={mode === 'link'} />
        <span className="hint">
          <span className="req">*</span>{' '}
          {mode === 'link'
            ? t('Pflichtfeld. Danach bekommen Sie einen Link, den Sie Ihrem Mitarbeiter schicken.')
            : t('Pflichtfeld. Ihre Kanzlei prüft alles und meldet sich, falls etwas fehlt.')}
        </span>
        {error && (
          <p className="formerr" role="alert">
            {error}
          </p>
        )}
        <div className="row">
          <button className="btn2 dark" type="submit" disabled={busy}>
            {busy ? t('Einen Moment …') : mode === 'link' ? t('Link erstellen') : t('Mitarbeiter anlegen')}
          </button>
          <button className="btn2" type="button" onClick={() => navigate(lohnPath(clientId))}>
            {t('Abbrechen')}
          </button>
        </div>
      </form>
    </>
  );
}

// Wieder einstellen (Sam 12.10.): Personalfragebogen mit den Angaben der alten Personalakte, nur der Eintritt ist neu.
// Der Mitarbeiter bekommt eine neue Personalakte, die Lohnbuchhaltung meldet ihn wieder an.
function RehirePage({ clientId, id }: { clientId: string; id: string }) {
  const { clientName } = useClientApp();
  const name = clientName(clientId);
  useLohnPage({ title: t('Wieder einstellen'), clientId, clientName: name, sub: t('Wieder einstellen') });
  const res = useApi<Rehire>(`/api/app/employee/${id}/wieder`);
  if (!res.data) return <Loading error={res.error} />;
  return <RehireForm clientId={clientId} firm={name} r={res.data} />;
}

function RehireForm({ clientId, firm, r }: { clientId: string; firm: string; r: Rehire }) {
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuestionnaire('alles', r.values, r);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const back = () => navigate({ pathname: lohnPath(clientId), search: `?ma=${r.id}` });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = q.check();
    if (problem) {
      toast(problem);
      return;
    }
    const ein = q.f.values.eintritt ?? '';
    if (ein <= r.lastDay) {
      const msg = t('Der neue Eintritt muss nach dem letzten Arbeitstag ({date}) liegen.', { date: fmtDate(r.lastDay) });
      q.f.setErrors({ eintritt: msg });
      document.getElementById(fieldId('eintritt'))?.focus();
      toast(msg);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/app/lohn/${clientId}/employees?wieder=${r.id}`, q.formData());
      toast(t('{name} ist wieder eingestellt · Ihre Lohnbuchhaltung hat die Angaben bekommen', { name: r.name }));
      navigate(lohnPath(clientId), { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hello">
        <h2>{t('{name} wieder einstellen', { name: r.name })}</h2>
        <p>
          {firm} · {t('bisher beschäftigt bis {date}', { date: fmtDate(r.lastDay) })}
        </p>
      </div>
      <form className="form maform" onSubmit={submit} noValidate>
        <p className="kdg-ok">
          {t(
            'Die Angaben aus der alten Personalakte sind schon eingetragen. Bitte tragen Sie den neuen Eintritt ein und prüfen Sie Adresse, Bankverbindung, Krankenkasse und Steuerklasse.',
          )}
        </p>
        {r.changes.length > 0 && (
          <div className="kdg-warn">
            {t('Diese Änderungen hat Ihre Kanzlei übernommen. Bitte prüfen Sie, ob sie im Formular stehen:')}
            <ul>
              {r.changes.map((c, i) => (
                <li key={i}>
                  {t(c.what)}: {c.value}
                </li>
              ))}
            </ul>
          </div>
        )}
        <QuestionnaireFields q={q} />
        <span className="hint">
          <span className="req">*</span> {t('Pflichtfeld. Ihre Kanzlei prüft alles und meldet sich, falls etwas fehlt.')}
        </span>
        {error && (
          <p className="formerr" role="alert">
            {error}
          </p>
        )}
        <div className="row">
          <button className="btn2 dark" type="submit" disabled={busy}>
            {busy ? t('Einen Moment …') : t('Wieder einstellen')}
          </button>
          <button className="btn2" type="button" onClick={back}>
            {t('Abbrechen')}
          </button>
        </div>
      </form>
    </>
  );
}
